import type { Locale } from '@/i18n/types';

/**
 * An airport with its city, as the dictionary returns it.
 *
 * Only the fields this app reads are declared. The endpoint returns more; adding
 * them here without using them would be a promise about a shape nobody verified.
 */
export interface AirportCity {
  id: number;
  airportIataCode: string | null;
  airportNameEn: string | null;
  airportNameRu: string | null;
  airportNameKz: string | null;
  cityNameEn: string | null;
  cityNameRu: string | null;
  cityNameKz: string | null;
  countryCode: string | null;
}

/**
 * A row of the airport dictionary — the shared master reference every schedule
 * integration matches flights against.
 *
 * This is the shape `/airport/list`, `/airport/id/{id}` and `/airport/code/{code}`
 * return, and it is also the request body for a create or an edit.
 *
 * **An edit is a REPLACE, not a patch.** The server expects the whole object back
 * with the changes applied, so a field left out of the body is a field being
 * cleared — including a column this app does not model. `AirportPanel` therefore
 * builds every edit body by spreading the row it loaded and overwriting only the
 * inputs, which carries such a column through untouched. Only the fields declared
 * here are typed; the spread preserves the rest whether or not TypeScript knows
 * about them.
 */
export interface Airport {
  id: number;
  /** Server-assigned; ignored in a request body. */
  created: string | null;
  updated: string | null;
  /** Soft-active flag. **Defaults to `false` when omitted from a create.** */
  active: boolean;
  /** The natural unique key. Trimmed and upper-cased by the server. */
  airportIataCode: string | null;
  airportNameRu: string | null;
  airportNameEn: string | null;
  airportNameKz: string | null;
  cityNameRu: string | null;
  cityNameEn: string | null;
  cityNameKz: string | null;
  /** ISO country code. Trimmed and upper-cased by the server. */
  countryCode: string | null;
  countryNameRu: string | null;
  countryNameEn: string | null;
  countryNameKz: string | null;
  /** An IANA zone id, e.g. `Asia/Almaty`. */
  localTimeZone: string | null;
  /** Decimal degrees, as STRINGS — the API's own choice, kept rather than parsed. */
  latitude: string | null;
  longitude: string | null;
  /**
   * Filterable and sortable through `/airport/list`, but absent from the documented
   * object — so it is declared optional and never assumed present.
   */
  icaoCode?: string | null;
}

/**
 * The five fields a create or an edit is rejected without.
 *
 * The English names and the two codes — the row has to be identifiable and readable
 * in at least one language.
 */
export const AIRPORT_REQUIRED = [
  'airportIataCode',
  'airportNameEn',
  'cityNameEn',
  'countryCode',
  'countryNameEn',
] as const;

export type AirportRequiredField = (typeof AIRPORT_REQUIRED)[number];

/**
 * What `/airport/list` accepts as `sortBy`.
 *
 * **These are not the field names.** The dictionary sorts by `iataCode` and `nameEn`
 * while the object it returns calls them `airportIataCode` and `airportNameEn`, so a
 * column cannot pass its own key through. Anything outside this list is answered
 * with a **400**, not a default order — which is why the URL is validated against it
 * before a request is built rather than after one fails.
 */
export const AIRPORT_SORT_FIELDS = [
  'iataCode',
  'icaoCode',
  'nameRu',
  'nameEn',
  'nameKz',
  'cityNameRu',
  'cityNameEn',
  'cityNameKz',
  'countryCode',
  'countryNameRu',
  'countryNameEn',
  'countryNameKz',
  'created',
  'updated',
  'id',
] as const;

export type AirportSortField = (typeof AIRPORT_SORT_FIELDS)[number];

/**
 * The ru/en/kz variant for the reader's language, falling back to English.
 *
 * Every name in this dictionary comes as a triple, and a row is often filled in for
 * one language only — so the fallback is what stops a Kazakh reader seeing a column
 * of blanks where the English names are perfectly good.
 */
export const localized = (
  locale: Locale,
  variants: { ru: string | null; en: string | null; kz: string | null },
): string =>
  (locale === 'ru' ? variants.ru : locale === 'kk' ? variants.kz : variants.en) ??
  variants.en ??
  '';

/**
 * The city an `AirportCity` row is in, in the reader's language.
 *
 * Falls back past `localized`'s own English fallback to the airport's name: a row with
 * no city filled in at all is still better labelled "Heathrow" than left blank, and a
 * blank here would read as the lookup having failed rather than as missing data.
 */
export const cityOf = (airport: AirportCity, locale: Locale): string =>
  localized(locale, {
    ru: airport.cityNameRu,
    en: airport.cityNameEn,
    kz: airport.cityNameKz,
  }) ||
  airport.airportNameEn ||
  '';

export const airportName = (airport: Airport, locale: Locale): string =>
  localized(locale, {
    ru: airport.airportNameRu,
    en: airport.airportNameEn,
    kz: airport.airportNameKz,
  });

export const airportCity = (airport: Airport, locale: Locale): string =>
  localized(locale, { ru: airport.cityNameRu, en: airport.cityNameEn, kz: airport.cityNameKz });

export const airportCountry = (airport: Airport, locale: Locale): string =>
  localized(locale, {
    ru: airport.countryNameRu,
    en: airport.countryNameEn,
    kz: airport.countryNameKz,
  });
